<script>
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import StatusBar from '@/V2/components/status/StatusBar.vue';
import { Statuses } from '@/V2/components/status/utils';
import {
  iButton,
  iButtonSizes,
  iButtonTypes,
  iButtonStyles,
} from '@inscribe/signal';
import {
  COMPLIANCE_RESULT,
  COMPLIANCE_RESULT_TO_READABLE_LABEL,
} from '@/constants/businessComplianceAgent';

export default {
  components: {
    ExpandableContainer,
    iButton,
    StatusBar,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    summary: {
      type: Object,
      default: () => {},
    },
    isComplianceRunning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      Statuses,
      iButtonSizes,
      iButtonTypes,
      iButtonStyles,
      COMPLIANCE_RESULT,
      COMPLIANCE_RESULT_TO_READABLE_LABEL,
    };
  },
  computed: {
    hasEmptyState() {
      return !this.summary?.aggregated_result_counts;
    },
    showEmptyState() {
      return this.loading || this.hasEmptyState || this.isComplianceRunning;
    },
    displayedResults() {
      const results = [
        COMPLIANCE_RESULT.FAIL,
        COMPLIANCE_RESULT.INCONCLUSIVE,
        COMPLIANCE_RESULT.PASS,
      ];
      if (
        this.summary?.aggregated_result_counts[
          COMPLIANCE_RESULT.NOT_APPLICABLE
        ] > 0
      ) {
        results.push(COMPLIANCE_RESULT.NOT_APPLICABLE);
      }
      return results;
    },
  },
  methods: {
    getBarWidth(result) {
      const total = Object.values(
        this.summary?.aggregated_result_counts || {},
      ).reduce((acc, curr) => acc + curr, 0);
      return total === 0 || !this.summary?.aggregated_result_counts[result]
        ? 0
        : parseInt(
            (this.summary?.aggregated_result_counts[result] / total) * 100,
          );
    },
  },
};
</script>

<template>
  <div
    class="customer-compliance-distribution"
    data-testid="customer-compliance-distribution"
  >
    <ExpandableContainer title="Check Distribution" :expandable="false">
      <template #items>
        <div class="distribution slot-content d-flex flex-column i-p-4 i-gap-2">
          <div
            class="bar d-flex flex-column i-gap-4 i-px-6 i-pb-6 i-pt-4"
            v-for="result in displayedResults"
            :key="result"
            :data-testid="`customer-fraud-distribution-bar-${result}`"
          >
            <div class="bar-label d-flex justify-content-between i-radius-md">
              <span class="label i-text-subheading-2 i-text-neutral-120">{{
                COMPLIANCE_RESULT_TO_READABLE_LABEL[result]
              }}</span>
              <span class="value i-text-subheading-2 i-text-neutral-120">{{
                showEmptyState
                  ? '0'
                  : summary?.aggregated_result_counts[result] || 0
              }}</span>
            </div>
            <div
              :class="{
                'linear-background': loading || isComplianceRunning,
              }"
              class="bar-fill i-radius-sm"
            >
              <div
                v-if="!showEmptyState"
                :class="{
                  pass: result === COMPLIANCE_RESULT.PASS,
                  fail: result === COMPLIANCE_RESULT.FAIL,
                  inconclusive: result === COMPLIANCE_RESULT.INCONCLUSIVE,
                  'not-applicable': result === COMPLIANCE_RESULT.NOT_APPLICABLE,
                }"
                class="fill i-radius-sm"
                :style="{ width: getBarWidth(result) + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </template>
    </ExpandableContainer>
  </div>
</template>

<style lang="scss" scoped>
.distribution {
  min-height: 196px;
  background: $white-base;
  height: 100%;

  .bar {
    .bar-fill {
      position: relative;
      height: 8px;
      width: 100%;
      background-color: $light-neutral-30;

      .fill {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        &.pass {
          background-color: $light-success-90;
        }

        &.fail {
          background-color: $light-error-90;
        }

        &.inconclusive {
          background-color: $light-warning-90;
        }

        &.not-applicable {
          background-color: $light-neutral-90;
        }
      }
    }
  }
}
</style>
